import { default as React } from 'react';
import { useEffect, useState } from 'react';
import { useAsync } from 'react-use';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  alertApiRef,
  configApiRef,
  errorApiRef,
  identityApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { WorkloadClient } from '@internal/workload-api-helper';
import { BaseEditIcon } from '../BaseEditIcon';
import { VpcSelectionDialog } from './ManagedVpcEditDialog';
import { Account, Vpc } from '../types';
import { isOkayResponse, isValueDefined } from '../utils/checks';
import { getMessage } from '../../../utils/getMessage';
import { SeverityEnum } from '../../../enums/SeverityEnum';

let accountObject: { [key: string]: string | undefined };

type RegionVpcCount = {
  [region: string]: number;
};

type WorkloadVpcDetails = {
  account_id: string;
  vpc_count: RegionVpcCount;
};

interface EditManagedVpcIconProps {
  vpcs: Vpc[];
  accountId: string;
  vpcData?: WorkloadVpcDetails;
  cardContentClass: string;
}

export const EditManagedVpcIcon = ({
  vpcs,
  accountId,
  vpcData,
  cardContentClass,
}: EditManagedVpcIconProps) => {
  const { entity } = useEntity();
  const identity = useApi(identityApiRef);
  const alertApi = useApi(alertApiRef);
  const errorApi = useApi(errorApiRef);
  const catalogApi = useApi(catalogApiRef);
  const configApi = useApi(configApiRef);
  const oauthApi = useApi(microsoftAuthApiRef);
  const workloadClient = new WorkloadClient(configApi, oauthApi);

  const [workloadData, setWorkloadData] = useState<Account>();
  const [requesterEmail, setRequesterEmail] = React.useState('');
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [loadingRequiredEditData, setLoadingRequiredEditData] =
    useState<boolean>(false);

  const fetchWorkloadData = async () => {
    return await workloadClient.apiFetch(`workloads/${accountId}`);
  };

  useAsync(async (): Promise<Account> => {
    setLoadingRequiredEditData(true);
    const [workloadDetails] = await Promise.all([fetchWorkloadData()]);

    const [details] = await Promise.all([workloadDetails.json()]);
    const { email } = await identity.getProfileInfo();
    if (email) {
      setRequesterEmail(email);
    }

    setWorkloadData(details);
    setLoadingRequiredEditData(false);

    return details;
  }, []);

  const parseWorkloadObjectValues = (
    catalogLocationStringList: string[] | undefined,
  ): { [key: string]: string | undefined } => {
    const resourceFileName: string | undefined =
      catalogLocationStringList?.pop();
    const resourceName = resourceFileName?.split('.')[0];
    const workload = catalogLocationStringList?.pop();

    return {
      resourceName,
      workload,
    };
  };

  useEffect(() => {
    async function getRequesterEmail() {
      const profile = await identity.getProfileInfo();
      setRequesterEmail(profile.email || '');
    }
    getRequesterEmail();
  }, [identity]);

  const handleClickOpen = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const stopLoadingAndCloseModal = () => {
    setIsModalOpen(false);
  };

  const killProcessAndReportError = (e: any) => {
    stopLoadingAndCloseModal();
    errorApi.post(e);
  };

  const callPatchApi = async (updated_vpcs: Vpc[]) => {
    const payload: { [key: string]: Object } = {
      vpc: updated_vpcs,
    };

    const stripCatalogLocation = workloadData?.catalogLocation;
    const catalogLocationStringList = stripCatalogLocation?.split('/');
    if (catalogLocationStringList) {
      accountObject = parseWorkloadObjectValues(catalogLocationStringList);
    } else {
      stopLoadingAndCloseModal();
      alertApi.post({
        message: getMessage({
          failureToDo: 'find your resource with the data provided',
        }).FAILURE,
        severity: SeverityEnum.ERROR,
      });
      return Promise.resolve(false);
    }

    if (isValueDefined(accountObject?.workload)) {
      try {
        const patchResourceDataResponse = await workloadClient.apiFetch(
          `/workloads/workload/${accountObject.workload}/resource/${accountObject.resourceName}`,
          'PATCH',
          payload,
        );

        if (!isOkayResponse(patchResourceDataResponse)) {
          stopLoadingAndCloseModal();
          alertApi.post({
            message: getMessage({
              failureToDo: 'update your resource with the data provided',
            }).FAILURE,
            severity: SeverityEnum.ERROR,
          });
        } else {
          alertApi.post({
            message: getMessage({
              successMessage:
                'Updating VPC information. Refresh this page to see the changes.',
            }).SUCCESS,
            severity: SeverityEnum.INFO,
          });

          await catalogApi.refreshEntity(stringifyEntityRef(entity));
        }
      } catch (e: any) {
        killProcessAndReportError(e);
      }
    }
    return Promise.resolve(true);
  };

  return (
    <>
      <BaseEditIcon
        workloadData={workloadData}
        requesterEmail={requesterEmail}
        handleClickOpen={handleClickOpen}
        isModalOpen={isModalOpen}
        handleClose={handleClose}
        handleConfirm={() => {}} // Will be handled by dialog
        disableConfirm={false}
        isLoading={false}
        isLoadingEdit={loadingRequiredEditData}
        dialogTitle="Update Foundation Managed VPC"
        allowEngineerEdit
        allowOwnerEdit={false}
        apiValidationPayload={{}}
      />
      <VpcSelectionDialog
        open={isModalOpen}
        handleClose={handleClose}
        callPatchApi={callPatchApi}
        workloadClient={workloadClient}
        accountId={accountId}
        vpcs={vpcs}
        vpcCount={vpcData?.vpc_count}
        cardContentClass={cardContentClass}
      />
    </>
  );
};
